import React, { useEffect, useState } from "react";
import { Calendar, Clock, Music, Phone, ChefHat, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useSection } from "@/context/SiteContext";
import { resolveImageUrl, telHref } from "@/lib/site";

function formatSeDate(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  const months = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1] || ""}`;
}

function weekdaySe(iso) {
  if (!iso) return "";
  const dt = new Date(iso + "T12:00:00");
  const days = ["Söndag", "Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag"];
  return days[dt.getDay()];
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const contact = useSection("contact");
  const tel = telHref(contact.phone_tel, contact.phone);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/events");
        setEvents(data || []);
      } catch {
        setEvents([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || events.length === 0) return null;
  const active = events[activeIdx] || events[0];
  const activeImg = resolveImageUrl(active.image_url);

  return (
    <section id="handelser" className="py-14 md:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-[var(--text)] text-white rounded-3xl overflow-hidden shadow-2xl">
          <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-gradient-to-br from-[#FFB663] via-[#E04F39] to-transparent opacity-40 blur-2xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-0">
            {/* Left intro */}
            <div className="lg:col-span-4 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-white/10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#FFB663]">
                <Music size={14} /> Vad händer
              </div>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-black tracking-tight mt-4 leading-[1.05]">
                Kommande <br />
                <span className="italic font-medium text-[#FFB663]">aktiviteter</span>
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                Trubadur, livemusik och andra roliga kvällar hos Mon Ami.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
                  <ChefHat size={14} className="text-[#FFB663]" /> {events.length} kommande
                </span>
              </div>
              <a
                href={tel}
                data-testid="events-call-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FFB663] hover:bg-[#FFA243] text-stone-900 px-5 py-3 font-bold transition-colors"
              >
                <Phone size={15} /> Boka bord
              </a>
            </div>

            {/* Right - event list + featured */}
            <div className="lg:col-span-8 p-8 lg:p-12">
              {/* Event tabs */}
              <div className="flex flex-wrap gap-1.5 mb-6">
                {events.slice(0, 8).map((e, i) => {
                  const isActive = i === activeIdx;
                  const day = e.date ? e.date.split("-")[2] : "—";
                  return (
                    <button
                      key={e.id}
                      onClick={() => setActiveIdx(i)}
                      data-testid={`event-tab-${i}`}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                        isActive
                          ? "bg-[#FFB663] text-stone-900 shadow-lg shadow-[#FFB663]/20"
                          : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <span className="font-serif-display text-base font-black leading-none tabular-nums">{day}</span>
                      <span className="hidden sm:inline">{formatSeDate(e.date).split(" ")[1]}</span>
                    </button>
                  );
                })}
              </div>

              {/* Featured event */}
              <article
                key={active.id}
                data-testid={`event-featured-${active.id}`}
                className="rounded-2xl overflow-hidden bg-white/5 border border-white/10"
              >
                <div className="grid sm:grid-cols-5 gap-0">
                  <div className="sm:col-span-2 relative aspect-[4/3] sm:aspect-auto bg-stone-900 overflow-hidden">
                    {activeImg ? (
                      <img
                        src={activeImg}
                        alt={active.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 grid place-items-center text-white/30">
                        <Music size={48} />
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-3 p-6 lg:p-8 space-y-3">
                    <div className="flex items-center gap-3 text-xs text-white/60 flex-wrap">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={12} className="text-[#FFB663]" />
                        {weekdaySe(active.date)} {formatSeDate(active.date)}
                      </span>
                      {active.time && (
                        <span className="inline-flex items-center gap-1.5">
                          <Clock size={12} className="text-[#FFB663]" /> {active.time}
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif-display text-2xl sm:text-3xl font-bold leading-tight">
                      {active.title}
                    </h3>
                    {active.description && (
                      <p className="text-sm text-white/70 leading-relaxed">{active.description}</p>
                    )}
                    <div className="pt-2">
                      <a
                        href={tel}
                        className="inline-flex items-center gap-2 rounded-full bg-[#FFB663] text-stone-900 px-5 py-2.5 font-semibold text-sm hover:bg-white transition-colors"
                        data-testid={`event-book-${active.id}`}
                      >
                        <Phone size={13} /> Boka bord till denna kväll
                      </a>
                    </div>
                  </div>
                </div>
              </article>

              <div className="mt-6 pt-5 border-t border-white/10 text-xs text-white/40 flex items-center gap-2">
                <ExternalLink size={11} /> Hanteras från admin · uppdateras live
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
